/**
 * Mapeo de nombres de equipo/página → emoji de bandera.
 * Las banderas nacionales son símbolos públicos, no copyright.
 *
 * Se usa el wordmark sin tildes para no fallar por encoding ("Mexico" o "México").
 */

const RAW: Record<string, string> = {
  // CONMEBOL
  argentina: '🇦🇷',
  brasil: '🇧🇷',
  brazil: '🇧🇷',
  uruguay: '🇺🇾',
  colombia: '🇨🇴',
  ecuador: '🇪🇨',
  peru: '🇵🇪',
  chile: '🇨🇱',
  paraguay: '🇵🇾',
  bolivia: '🇧🇴',
  venezuela: '🇻🇪',
  // CONCACAF
  mexico: '🇲🇽',
  'estados unidos': '🇺🇸',
  'united states': '🇺🇸',
  usa: '🇺🇸',
  canada: '🇨🇦',
  'costa rica': '🇨🇷',
  jamaica: '🇯🇲',
  panama: '🇵🇦',
  haiti: '🇭🇹',
  'el salvador': '🇸🇻',
  honduras: '🇭🇳',
  curacao: '🇨🇼',
  // UEFA
  francia: '🇫🇷',
  france: '🇫🇷',
  espana: '🇪🇸',
  spain: '🇪🇸',
  inglaterra: '🇬🇧',
  england: '🇬🇧',
  alemania: '🇩🇪',
  germany: '🇩🇪',
  italia: '🇮🇹',
  italy: '🇮🇹',
  portugal: '🇵🇹',
  'paises bajos': '🇳🇱',
  netherlands: '🇳🇱',
  belgium: '🇧🇪',
  belgica: '🇧🇪',
  switzerland: '🇨🇭',
  suiza: '🇨🇭',
  austria: '🇦🇹',
  croatia: '🇭🇷',
  croacia: '🇭🇷',
  turkey: '🇹🇷',
  turkiye: '🇹🇷',
  turquia: '🇹🇷',
  denmark: '🇩🇰',
  poland: '🇵🇱',
  serbia: '🇷🇸',
  ukraine: '🇺🇦',
  scotland: '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
  wales: '🏴󠁧󠁢󠁷󠁬󠁳󠁿',
  sweden: '🇸🇪',
  norway: '🇳🇴',
  czechia: '🇨🇿',
  'czech republic': '🇨🇿',
  'bosnia and herzegovina': '🇧🇦',
  'bosnia-herzegovina': '🇧🇦',
  'bosnia herzegovina': '🇧🇦',
  bosnia: '🇧🇦',
  // CAF
  marruecos: '🇲🇦',
  morocco: '🇲🇦',
  senegal: '🇸🇳',
  ghana: '🇬🇭',
  egypt: '🇪🇬',
  egipto: '🇪🇬',
  'ivory coast': '🇨🇮',
  "cote d'ivoire": '🇨🇮',
  algeria: '🇩🇿',
  argelia: '🇩🇿',
  nigeria: '🇳🇬',
  cameroon: '🇨🇲',
  camerun: '🇨🇲',
  tunisia: '🇹🇳',
  tunez: '🇹🇳',
  'south africa': '🇿🇦',
  'cape verde': '🇨🇻',
  'cabo verde': '🇨🇻',
  'congo dr': '🇨🇩',
  'dr congo': '🇨🇩',
  'democratic republic of congo': '🇨🇩',
  // AFC
  japon: '🇯🇵',
  japan: '🇯🇵',
  corea: '🇰🇷',
  korea: '🇰🇷',
  'south korea': '🇰🇷',
  'korea republic': '🇰🇷',
  'republica de corea': '🇰🇷',
  australia: '🇦🇺',
  'saudi arabia': '🇸🇦',
  iran: '🇮🇷',
  'ir iran': '🇮🇷',
  qatar: '🇶🇦',
  iraq: '🇮🇶',
  jordan: '🇯🇴',
  jordania: '🇯🇴',
  uzbekistan: '🇺🇿',
  'united arab emirates': '🇦🇪',
  uae: '🇦🇪',
  // OFC
  'new zealand': '🇳🇿',
};

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();
}

export function flagFor(name?: string | null): string | null {
  if (!name) return null;
  return RAW[normalize(name)] ?? null;
}

/**
 * Iconos especiales para páginas no-país (Intro, Museum, Coca-Cola).
 * Devuelve un emoji o null si no aplica.
 */
export function specialIconFor(title?: string | null): string | null {
  if (!title) return null;
  const t = title.toLowerCase().trim();
  if (t === 'fifa museum' || t === 'museum' || t === 'history') return '🏛️';
  if (t === 'intro') return '✦';
  return null;
}

/**
 * Devuelve true si el título es Coca-Cola (que se renderiza con un componente
 * especial: C blanca sobre fondo rojo).
 */
export function isCocaCola(title?: string | null): boolean {
  if (!title) return false;
  const t = title.toLowerCase().trim();
  return t === 'coca-cola' || t === 'coca cola' || t === 'cocacola';
}

/**
 * Normaliza un nombre para comparación (lowercase, sin acentos, sin signos).
 */
function searchNormalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Aliases para que busques en inglés/sin acentos y matchee el nombre oficial del álbum
const TITLE_ALIASES: Record<string, string[]> = {
  'cote d ivoire':       ['ivory coast', 'cote divoire', 'costa de marfil', 'civ'],
  'korea republic':      ['south korea', 'corea', 'corea del sur', 'kor'],
  'cabo verde':          ['cape verde', 'islas cabo verde', 'cpv'],
  'ir iran':             ['iran', 'islamic republic of iran', 'islamica iran', 'irn'],
  'bosnia herzegovina':  ['bosnia and herzegovina', 'bosnia', 'bih'],
  'congo dr':            ['dr congo', 'democratic republic of congo', 'congo democratico', 'cod'],
  'turkiye':             ['turkey', 'turquia', 'tur'],
  'curacao':             ['curaçao', 'cuw'],
  'usa':                 ['united states', 'estados unidos', 'us'],
  'czechia':             ['czech republic', 'republica checa', 'cze'],
  'south africa':        ['sudafrica', 'rsa'],
  'new zealand':         ['nueva zelanda', 'nzl'],
  'saudi arabia':        ['arabia saudita', 'arabia saudi', 'ksa'],
  'germany':             ['alemania', 'ger', 'deu'],
  'france':              ['francia', 'fra'],
  'spain':               ['espana', 'esp'],
  'england':             ['inglaterra', 'eng', 'gbr'],
  'brazil':              ['brasil', 'bra'],
  'argentina':           ['arg'],
  'mexico':              ['mex'],
  'japan':               ['japon', 'jpn'],
  'netherlands':         ['paises bajos', 'holanda', 'ned'],
  'belgium':             ['belgica', 'bel'],
  'portugal':            ['por'],
  'uruguay':             ['uru'],
  'colombia':            ['col'],
  'australia':           ['aus'],
  'switzerland':         ['suiza', 'sui'],
  'austria':             ['aut'],
  'croatia':             ['croacia', 'cro'],
  'morocco':             ['marruecos', 'mar'],
  'senegal':             ['sen'],
  'egypt':               ['egipto', 'egy'],
  'ghana':               ['gha'],
  'algeria':             ['argelia', 'alg'],
  'iraq':                ['irak', 'irq'],
  'jordan':              ['jordania', 'jor'],
  'qatar':               ['qat'],
  'uzbekistan':          ['uzbekistan', 'uzb'],
  'ecuador':             ['ecu'],
  'paraguay':            ['par'],
  'panama':              ['pan'],
  'haiti':               ['hai'],
  'scotland':            ['escocia', 'sco'],
  'sweden':              ['suecia', 'swe'],
  'norway':              ['noruega', 'nor'],
  'tunisia':             ['tunez', 'tun'],
  'canada':              ['can'],
};

/**
 * Devuelve true si el query matchea el título de la página, con tolerancia para
 * acentos, alias en inglés/español, y prefix-match.
 */
export function pageMatchesQuery(title: string, query: string): boolean {
  const nTitle = searchNormalize(title);
  const nQuery = searchNormalize(query);
  if (!nQuery) return false;

  // Direct match (full, startsWith, includes, word prefix)
  const matches = (target: string) =>
    target === nQuery ||
    target.startsWith(nQuery) ||
    target.includes(nQuery) ||
    target.split(' ').some((w) => w.startsWith(nQuery));

  if (matches(nTitle)) return true;

  // Aliases registrados
  const aliases = TITLE_ALIASES[nTitle] ?? [];
  for (const a of aliases) {
    if (matches(searchNormalize(a))) return true;
  }
  return false;
}

/**
 * Match flexible para el code de la lainita (ej DB "CIV-2" vs query "civ2" / "civ 2").
 * Strippeamos hyphens, espacios y caracteres no alfanuméricos en ambos lados.
 */
export function codeMatchesQuery(code: string | null | undefined, query: string): boolean {
  if (!code) return false;
  const strip = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const nQuery = strip(query);
  if (!nQuery) return false;
  return strip(code).includes(nQuery);
}
